import { create } from "zustand";
import { Tables } from "../database.types";
import { supabaseClient } from "../shared/supabase";

export type Season = Tables<"seasons">;

type SeasonState = {
  currentSeason: Season | null;
  latestSeason: Season | null;
  isLoading: boolean;
  error: Error | null;
  lastFetched: number | null;
  isInitialized: boolean;
};

type SeasonStore = {
  initialize: () => Promise<void>;
  getCurrentSeason: () => Promise<void>;
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

export const selectSeasons = (
  seasons: Season[],
  today = new Date().toISOString().slice(0, 10),
) => {
  const startedSeasons = seasons
    .filter((season) => season.start_date <= today)
    .sort((first, second) =>
      second.start_date.localeCompare(first.start_date),
    );

  return {
    currentSeason:
      startedSeasons.find((season) => season.end_date >= today) ?? null,
    latestSeason: startedSeasons[0] ?? null,
  };
};

export const useSeasonStore = create<SeasonStore & SeasonState>((set, get) => ({
  currentSeason: null,
  latestSeason: null,
  isLoading: false,
  error: null,
  lastFetched: null,
  isInitialized: false,

  initialize: async () => {
    const state = get();
    if (state.isInitialized) return;

    await get().getCurrentSeason();
    set({ isInitialized: true });
  },

  getCurrentSeason: async () => {
    const state = get();

    // Check if we have cached data and it's still valid
    if (
      state.lastFetched &&
      Date.now() - state.lastFetched < CACHE_DURATION
    ) {
      return;
    }

    set({ isLoading: true, error: null });

    try {
      const { data: seasons, error } = await supabaseClient
        .from("seasons")
        .select("*")
        .order("end_date", { ascending: false });

      if (error) throw error;

      const { currentSeason, latestSeason } = selectSeasons(seasons);

      set({
        currentSeason,
        latestSeason,
        isLoading: false,
        error: null,
        lastFetched: Date.now(),
      });
    } catch (error) {
      set({
        isLoading: false,
        error: error as Error,
      });
      throw error; // Re-throw to handle in the UI
    }
  },
}));
